import { EChartOption } from 'echarts'
import { FC, useMemo } from 'react'
import { EChart } from 'src/charts/EChart'
import { ReviewComparisonTrendSeries } from 'src/services/reviewAnalysisApi/interfaces/reviewComparison.interface'
import styles from './ratingTrend.module.scss'
import { facilityColor } from '../palette'

interface RatingTrendProps {
  // The window's own label, e.g. "Jun 1 – Jun 30 2026".
  period: string
  series: ReviewComparisonTrendSeries[]
  weeks: string[]
}

const COPY = {
  subtitle: (period: string) => `Weekly average rating, ${period}`,
  title: 'Rating Trend',
}

// Ratings are out of five, and the axis says so whatever the data does: a
// chart that rescaled to the range in view would make a 3.4 and a 4.1 look
// further apart in a quiet month than in a loud one.
const MAX_RATING = 5

const CHART_HEIGHT = 330

export const RatingTrend: FC<RatingTrendProps> = ({
  period,
  series,
  weeks,
}) => {
  const options = useMemo<EChartOption>(
    () => ({
      grid: { bottom: 56, left: 44, right: 24, top: 24 },
      legend: {
        bottom: 0,
        itemGap: 24,
        textStyle: {
          color: '#25262d',
          fontFamily: 'Roboto, sans-serif',
          fontSize: 14,
        },
      },
      series: series.map((entry, index) => {
        const color = facilityColor(index)

        return {
          areaStyle: {
            color: {
              colorStops: [
                { color: `${color}2e`, offset: 0 },
                { color: `${color}00`, offset: 1 },
              ],
              type: 'linear',
              x: 0,
              x2: 0,
              y: 0,
              y2: 1,
            },
          },
          // Null is a gap in the line — the week nobody rated — which is
          // what echarts draws for it, even though its types say number[].
          data: entry.values as number[],

          // Hollow markers: the point is a reading, and a filled dot at this
          // size reads as a thickening of the line instead.
          itemStyle: { borderColor: color, borderWidth: 2, color: '#ffffff' },

          // The value sits over its own point: with two lines this close
          // together, a shared tooltip is the only other way to read them, and
          // a printed report has no hover.
          label: {
            color: '#25262d',
            fontFamily: 'Roboto, sans-serif',
            fontSize: 12,
            formatter: ({ value }: { value: number }) => value.toFixed(1),
            position: 'top',
            show: true,
          },

          lineStyle: { color, width: 1.5 },

          name: entry.facilityName,
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          type: 'line',
        }
      }),
      tooltip: {
        axisPointer: { type: 'none' },
        backgroundColor: '#ffffff',
        borderColor: '#eef1fe',
        extraCssText: 'box-shadow: 0 8px 24px #25262d1f; border-radius: 12px;',
        textStyle: {
          color: '#25262d',
          fontFamily: 'Roboto, sans-serif',
          fontSize: 12,
        },
        trigger: 'axis',
      },
      xAxis: {
        axisLabel: {
          color: '#52525b',
          fontFamily: 'Roboto, sans-serif',
          fontSize: 12,
          // Only the two ends are named. The weeks between them are read off
          // the line, and labelling all five crowds a 1170px card.
          formatter: (value: string, index: number) =>
            index === 0 || index === weeks.length - 1 ? value : '',
          margin: 12,
        },
        axisLine: { lineStyle: { color: '#b9c1d4' } },
        axisTick: { show: false },
        boundaryGap: false,
        data: weeks,
        type: 'category',
      },
      yAxis: {
        axisLabel: {
          color: '#52525b',
          fontFamily: 'Roboto, sans-serif',
          fontSize: 12,
          formatter: (value: number) => value.toFixed(1),
        },
        axisLine: { show: false },
        interval: 1,
        max: MAX_RATING,
        min: 0,
        splitLine: { lineStyle: { color: '#d3daea', type: 'dashed' } },
        type: 'value',
      },
    }),
    [series, weeks]
  )

  return (
    <section>
      <div className={styles.header}>
        <h3 className={styles.title}>{COPY.title}</h3>
        <p className={styles.subtitle}>{COPY.subtitle(period)}</p>
      </div>

      <div className={styles.chart}>
        <EChart height={CHART_HEIGHT} options={options} />
      </div>
    </section>
  )
}
